// Hierarchy of Control levels — these don't change (legislative ranking)
// so they stay client-side rather than being admin-managed.
export const HOC_LEVELS = [
  { rank: 1, label: 'Elimination' },
  { rank: 2, label: 'Substitution' },
  { rank: 3, label: 'Engineering' },
  { rank: 4, label: 'Isolation' },
  { rank: 5, label: 'Administrative' },
  { rank: 6, label: 'PPE (last)' },
] as const;

export const INFO_CONTENT: Record<string, { title: string; body: string }> = {
  stop: {
    title: 'STOP · Why this matters',
    body: `<p>The <strong>STOP</strong> phase is your chance to pause before starting work. Rushing past this step is the #1 cause of workplace incidents.</p>
    <p><strong>What "stop" means on a Harley's job:</strong></p>
    <ul><li>Confirm the job details match what's in front of you</li><li>Tell us what PPE you have on right now</li><li>If anything looks different from the job sheet · call your supervisor</li></ul>
    <p><strong>Legal basis:</strong> WHS Act 2020 (WA) s.84 · you have the right to cease work if you believe continuing poses a serious risk.</p>`,
  },
  ppe: {
    title: 'Personal Protective Equipment (PPE)',
    body: `<p><strong>PPE is the LAST line of defence</strong> · never the first. The Hierarchy of Control (next step) explains why.</p>
    <p><strong>Before ticking a box:</strong></p>
    <ul><li>Is the PPE in good condition?</li><li>Is it rated for this task (e.g. cut-resistant gloves vs nitrile gloves)?</li><li>Does it fit properly?</li></ul>
    <p>If you don't have the right PPE, <strong>do not start the task</strong>. Contact your supervisor.</p>`,
  },
  hazardCat: {
    title: 'Hazard Categories',
    body: `<p>A <strong>hazard</strong> is anything with the potential to cause harm. A <strong>category</strong> groups similar hazards.</p>
    <p><strong>WA WHS high-risk categories</strong> (require extra controls per Schedule 5 of WHS Reg 2022):</p>
    <ul><li>Working at heights (>2m)</li><li>Electrical work on energised parts</li><li>Confined space entry</li><li>Hot work (grinding, welding, open flame)</li><li>Isolation / Lockout-Tagout (LOTO)</li></ul>
    <p>Ticking a high-risk category may require a SWMS.</p>`,
  },
  risk: {
    title: 'Risk Assessment (Likelihood × Consequence)',
    body: '__RISK_MATRIX__',
  },
  hoc: {
    title: 'Hierarchy of Control',
    body: `<p>Ranked <strong>most effective (top)</strong> to <strong>least effective (bottom)</strong>. Always apply the highest practicable control.</p>
    <ul>
      <li><strong>1. Elimination</strong> · Remove the hazard entirely</li>
      <li><strong>2. Substitution</strong> · Replace with something safer</li>
      <li><strong>3. Engineering</strong> · Guards, ventilation, handrails</li>
      <li><strong>4. Isolation</strong> · LOTO, barriers, exclusion zones</li>
      <li><strong>5. Administrative</strong> · Procedures, training, signage</li>
      <li><strong>6. PPE</strong> · Personal Protective Equipment. Last line of defence only.</li>
    </ul>`,
  },
  regCheck: {
    title: 'Regulatory Checks',
    body: `<p>Aligned with the WA WHS Act 2020 and WHS Regulations 2022.</p>
    <ul>
      <li><strong>SWMS</strong> · Safe Work Method Statement, required for high-risk construction work (Reg 291)</li>
      <li><strong>LOTO</strong> · Lockout-Tagout procedure</li>
      <li><strong>Permits</strong> · Confined space (Reg 76) and hot work · must be in writing and on site</li>
      <li><strong>SDS</strong> · Safety Data Sheet for every hazardous chemical (Reg 344)</li>
    </ul>
    <p>If you answer "No" to a required check on a high-risk activity, <strong>do not proceed</strong> until resolved.</p>`,
  },
  reassess: {
    title: 'Your Right to Stop',
    body: `<p>Under <strong>WHS Act 2020 s.84</strong>, you have the right · and the obligation · to cease work if you believe continuing would expose you or others to serious risk.</p>
    <p>You will <strong>not</strong> be penalised by Harley's Plumbing for stopping work in good faith.</p>`,
  },
  likelihood: {
    title: 'Likelihood',
    body: `<ul><li><strong>1 · Rare:</strong> may occur only in exceptional circumstances</li><li><strong>2 · Unlikely:</strong> could occur occasionally</li><li><strong>3 · Possible:</strong> might occur at some time</li><li><strong>4 · Likely:</strong> will probably occur</li><li><strong>5 · Almost certain:</strong> expected in most circumstances</li></ul>`,
  },
  consequence: {
    title: 'Consequence',
    body: `<ul><li><strong>1 · Negligible:</strong> minor first aid</li><li><strong>2 · Minor:</strong> medical treatment required</li><li><strong>3 · Moderate:</strong> lost time injury</li><li><strong>4 · Major:</strong> permanent disability</li><li><strong>5 · Catastrophic:</strong> fatality</li></ul>`,
  },
};
